import { Fragment, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, ReceiptTextIcon } from 'lucide-react';

import { Badge } from '@nesso/ui/primitives/badge';
import { Button } from '@nesso/ui/primitives/button';
import { TableCell, TableRow } from '@nesso/ui/primitives/table';

import { formatPaymentMethod, formatPaymentNumber, type Payment } from '@/entities/payment';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericDate } from '@/shared/lib/time-utils';

import { ClientBillingSheetActivityPaymentDetails } from './client-billing-sheet-activity-payment/client-billing-sheet-activity-payment-details';

type Props = {
    payment: Payment;
    isCompact: boolean;
    getServiceLabel: (serviceCode: string) => string;
    onOpenAppliedTo: (paymentId: string, sessionChargeId: string) => void;
    onRefund: (payment: Payment) => void;
};

export function ClientBillingSheetActivityPayment({
    payment,
    isCompact,
    getServiceLabel,
    onOpenAppliedTo,
    onRefund,
}: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const number = formatPaymentNumber(payment.number);

    return (
        <Fragment>
            <TableRow className="h-18">
                <TableCell className="font-semibold">{formatNumericDate(payment.paidAt)}</TableCell>
                <TableCell>
                    <div className="flex items-center gap-1.5">
                        <ReceiptTextIcon className="size-4.5 shrink-0" />
                        {number}
                    </div>
                </TableCell>
                {!isCompact && (
                    <>
                        <TableCell>
                            {formatPaymentMethod(payment.method, payment.checkNumber)}
                        </TableCell>
                        <TableCell>
                            <div className="flex flex-col items-start gap-1">
                                {payment.appliedTo.map((appliedTo) => (
                                    <Badge
                                        key={appliedTo.sessionChargeId}
                                        variant="outline"
                                        asChild
                                    >
                                        <button
                                            type="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                onOpenAppliedTo(
                                                    payment.id,
                                                    appliedTo.sessionChargeId,
                                                )
                                            }
                                        >
                                            {formatNumericDate(appliedTo.date)},{' '}
                                            {appliedTo.serviceCode}
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </TableCell>
                    </>
                )}
                <TableCell className="text-right">
                    {formatCurrencyFromCents(payment.amountCents)}
                </TableCell>
                <TableCell className="py-2 pr-1 pl-0.5 text-right">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${number}`}
                        onClick={() => setIsExpanded((expanded) => !expanded)}
                    >
                        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Button>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow variant="detail">
                    <TableCell colSpan={isCompact ? 4 : 6} className="px-6 py-6 whitespace-normal">
                        <ClientBillingSheetActivityPaymentDetails
                            payment={payment}
                            isCompact={isCompact}
                            getServiceLabel={getServiceLabel}
                            onOpenAppliedTo={onOpenAppliedTo}
                            onRefund={onRefund}
                        />
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}
